// A familiar follows its owner (issue 1031, TOKEN-020). `follows` on a token names the token it travels with; the
// link is opt-in, made by the DM or by the one player who owns both tokens, and only between tokens one player owns
// alone — or two of the DM's own, owned by nobody else. A link to an enemy is refused. When a leader moves, every
// follower (and its followers) moves by the same offset along the same route, in the SAME event: the reducer expands
// the move from the state, so the log, a replay and every screen agree. A locked follower stays (design/11 §7), and
// so does one the DM is placing, one whose leader someone who sees the follower cannot see (its walk would trace the
// hidden leader's), and one the move would carry off the board.
import { canSee } from './objects.js';
/** Why `id` may not follow `leader` (null: it may). The owners rule and the circle; existence is the caller's. */
export function mayLink(state, id, leader) {
    if (leader === null)
        return null;
    const a = state.objects[id];
    const b = state.objects[leader];
    if (!a || !b)
        return 'It is no longer on the table.';
    if (id === leader)
        return 'A token cannot follow itself.';
    if (!sameSoleOwner(a, b))
        return 'Only tokens one player owns alone can follow each other.';
    // A circle would move forever: walk the leader's own chain.
    const seen = new Set();
    for (let at = leader; at && !seen.has(at); at = state.objects[at]?.follows) {
        if (at === id)
            return 'That would make a circle.';
        seen.add(at);
    }
    return null;
}
/** The creatures riding a token: `mounted` on a token that follows it. The rule answering a hit on a mount is its rider's. */
export const ridersOf = (state, mount) => Object.values(state.objects).filter((o) => o.follows === mount && o.mounted).map((o) => o.id);
/** Both owned by the same one player, or both by nobody but the DM. */
export function sameSoleOwner(a, b) {
    if (a.owners.length === 0 && b.owners.length === 0)
        return true;
    return a.owners.length === 1 && b.owners.length === 1 && a.owners[0] === b.owners[0];
}
/**
 * The followers' moves for some moves: each follower of a moved thing travels the leader's route shifted by where it
 * stood from the leader, then its own followers from it. A thing already moved (named in `moves`, or reached once)
 * is never moved twice. One stays, and so do its followers, when it is locked; when the DM is placing it (their
 * placement stands, as against a direct move); when a player who sees it cannot see its leader — hidden, being
 * placed, or under the fog — since its walk would trace the leader's (hidden means absent); and when the move would
 * carry any of it off the board.
 */
export function followMoves(state, moves) {
    const all = Object.values(state.objects);
    if (!all.some((o) => o.follows))
        return [];
    const done = new Set(moves.map((m) => m.id));
    const queue = moves.map((m) => ({ id: m.id, route: m.route }));
    const out = [];
    const scope = state.fog && state.grid ? { fog: state.fog, grid: state.grid } : null;
    // Someone at the table who owns neither: what they see is what a follower's walk must not give away.
    const anyone = { role: 'player' };
    const extent = state.fog?.extent ?? null;
    const onBoard = (p) => p.x >= 0 && p.y >= 0 && (!extent || (p.x <= extent.width && p.y <= extent.height));
    while (queue.length) {
        const lead = queue.shift();
        const from = state.objects[lead.id];
        if (!from)
            continue;
        const leaderSeen = canSee(from, anyone, scope);
        for (const o of all) {
            if (o.follows !== lead.id || done.has(o.id) || o.locked || o.held)
                continue;
            if (!leaderSeen && canSee(o, anyone, scope))
                continue;
            const dx = o.x - from.x;
            const dy = o.y - from.y;
            const route = lead.route.map((p) => ({ x: p.x + dx, y: p.y + dy }));
            if (!route.every(onBoard))
                continue;
            done.add(o.id);
            out.push({ id: o.id, route, rev: o.rev });
            queue.push({ id: o.id, route });
        }
    }
    return out;
}
//# sourceMappingURL=follow.js.map